import { eventOnCalendarDay, calendarMinutes } from '../../../../../../platform/util/calendarTimezone';
import React, { useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { format } from 'date-fns';
import { formatCalendarDate } from '../../../../../../platform/util/calendarSystem';
import { useCalendar } from '../calendarContext';
import WeekNavigation from '../week/weekNavigation';
import BookItOrCreate from '../bookItOrCreate';
import EventScheduler from '../day/eventScheduler';
import { filterByBookingTypes, normalizeBookings } from '../shared/eventFilters';
import { calendarSurfaceSx } from '../shared/viewSurface';
import CompactAgenda from '../day/compactAgenda';

const DayView = ({ currentDate, bookingData, showShortWeekCalendar = true, showTimeSlots = true, compact = false, monthAgenda = false }) => {
  const { selectedDate, myBookingTypes, bookingDatas, displayTimezone, displayCalendar } = useCalendar();

  const dataToProcess = normalizeBookings(bookingData, bookingDatas);
  const filteredBookings = useMemo(
    () => filterByBookingTypes(dataToProcess, myBookingTypes),
    [dataToProcess, myBookingTypes]
  );
  const bookItOrCreateRef = useRef(null);
  const isEmbeddedTimeline = !showShortWeekCalendar && !showTimeSlots;
  const desiredDate = currentDate || selectedDate
  const getDisplayTitle = (event) => {
    const rawTitle =
      event?.bookingTypeName ||
      event?.title ||
      event?.summary ||
      event?.inviteeName ||
      "Event";
    const normalized = String(rawTitle || "").trim();
    return normalized || "Event";
  };

  const getEventsForHour = (hour) => {
    // Filter events whose start time falls within this hour range
    const eventsForHour = filteredBookings.filter((event) => {
      const eventStart = new Date(event.time);
      if (!event.isAllDay) {
        return eventOnCalendarDay(event, desiredDate, displayTimezone) && Math.floor(calendarMinutes(eventStart, displayTimezone) / 60) === hour;
      }
      else {
        return hour === 0 && eventOnCalendarDay(event, desiredDate, displayTimezone);
      }
    }).map((event) => {
      const startTime = new Date(event.time);
      const bookingType = (myBookingTypes || []).find(
        (val) => parseInt(val.id) === event.bookingTypeId
      );
      const source = String(event?.source || "").toLowerCase();
      const isGoogleEvent = source.includes("google");
      const isQuickBooking =
        event?.bookingTypeId == null ||
        Boolean(event?.quickSource) ||
        Boolean(event?.quickTitle);
      if (!bookingType && !isGoogleEvent && !isQuickBooking) return null;

      const fallbackBookingType = {
        id: event.bookingTypeId || "google-event",
        title: getDisplayTitle(event),
        color: isQuickBooking ? "#0ea5e9" : "#34a853",
        length: event.duration || 60,
      };

      const resolvedBookingType = bookingType || fallbackBookingType;
      const durationMinutes = event.duration || resolvedBookingType?.length || 60;
      const explicitEnd = event.endTime ? new Date(event.endTime) : null;
      const endTime = explicitEnd && explicitEnd > startTime ? explicitEnd : new Date(startTime.getTime() + durationMinutes * 60000);
      return { startTime, endTime, bookingType: resolvedBookingType, event };
    }).filter(Boolean);

    return eventsForHour;
  };


  if (monthAgenda) return <CompactAgenda date={desiredDate} events={filteredBookings} monthAgenda />;

  const content = (
    <>
      {compact && <Typography component="h2" sx={{ px: 1, py: 1, fontSize: 13, fontWeight: 500, bgcolor: 'background.paper' }}>
        {formatCalendarDate(format(desiredDate, 'yyyy-MM-dd'), displayCalendar)} · {format(desiredDate, 'EEEE')}
      </Typography>}
      {showShortWeekCalendar && !compact && (
        <WeekNavigation />
      )}
      <Box>
      <EventScheduler
        compact={compact}
        events={filteredBookings}
        getEventsForHour={getEventsForHour}
        bookItOrCreateRef={bookItOrCreateRef}
        showTimeSlots={showTimeSlots}
        BookItOrCreate={BookItOrCreate}
        currentDate={desiredDate}
      />
      </Box>
    </>
  );

  if (isEmbeddedTimeline) {
    return <Box sx={{ width: '100%' }}>{content}</Box>;
  }

  return (
    <Box sx={{ ...calendarSurfaceSx, p: compact ? 0 : { xs: 1, sm: 1.25 }, borderRadius: compact ? 0 : 2, overflow: 'hidden' }}>
      {content}
    </Box>
  );
};

export default DayView;
